import * as React from "react";

import { DetailsList, DetailsListLayoutMode, IColumn, Selection, SelectionMode, CheckboxVisibility } from "@fluentui/react/lib/DetailsList";
import { PrimaryButton, DefaultButton } from "@fluentui/react/lib/Button";
import { MarqueeSelection } from "@fluentui/react/lib/MarqueeSelection";
import { entityCriteriaScreen, toggleLoading } from "../actions/navigationWorker";
import { formatDate } from "../actions/helper";
import { fileExistingItem } from "../actions/FilerWorker";
import { getQueuedEmails } from "../actions/queueWorker";

export interface AppProps {
    hide: boolean;
    data: any[];
}

export interface AppState {
    columns: IColumn[],
    selected: any,
    data: any,
    showConfirmation: boolean,
    queuedEmailCount: number
}

class ExistingDocNote extends React.Component<AppProps, AppState> {
	private _selection: Selection;

	constructor(props) {
		super(props);

		this._selection = new Selection({
			onSelectionChanged: () => {
				this.setState({
					selected: this._getSelectionDetails()
				});
			}
		});

		this.state = {
			columns: this._getColumns(),
			selected: null,
			data: this._getItems(),
			showConfirmation: false,
			queuedEmailCount: 0
		};
	}

	componentDidMount() {
		// Listen for queue updates to refresh the component
		window.addEventListener('queueUpdated', this.handleQueueUpdate);
	}

	componentWillUnmount() {
		window.removeEventListener('queueUpdated', this.handleQueueUpdate);
	}

	handleQueueUpdate = () => {
		// Force re-render when queue changes
		this.forceUpdate();
	}

	NewDoc = () => {
		entityCriteriaScreen(false);
	};

	handleUpdateButtonClick = () => {
		// Check if there are queued emails
		const queuedEmails = getQueuedEmails();
		
		if (queuedEmails.length > 0) {
			// Show confirmation dialog if there are any queued emails
			this.setState({
				showConfirmation: true,
				queuedEmailCount: queuedEmails.length
			});
		} else {
			// No queue - proceed directly with current email
			this.Select_file_to();
		}
	}

	confirmFileQueue = () => {
		// Hide confirmation dialog
		this.setState({ showConfirmation: false });
		// Proceed with filing
		this.Select_file_to();
	}

	cancelFileQueue = () => {
		// Just hide the confirmation dialog
		this.setState({ showConfirmation: false });
	}

	private _getItems(): any {
		const items = [];
		if (!this.props.data) {
			return items;
		}

		for (const item of this.props.data) {
			const subject = `|break|Subject: ${item.subject}`;
			const created = formatDate(new Date(item.created_date?._val));
			const created_string = "|break|Created: " + created;

			const fieldRow = {
				id: item.docid,
				entityId: item.entity_ids,
				entity_name: item.entity_names + subject + created_string,
				subject: `|break|Subject: ${item.subject}`,
				created

			};

			if (!items.some(x => x.id === fieldRow.id)) { items.push(fieldRow); }
		}

		return items.sort((a, b) => a.subject.localeCompare(b.subject));
	}

	private _getColumns(): IColumn[] {
		const columns = [];
		const fields = [
			{
				"field": "entity_name",
				"header": "Note Details",
				"min": 100
			},
		];

		for (let i = 0; i < fields.length; i++) {
			const field = fields[i];
			const item_column = {
				key: "col_" + field["field"],
				name: field["header"],
				fieldName: field["field"],
				minWidth: field["min"],
				isResizable: true,
				isMultiline: true
			};
			columns.push(item_column);
		}
		return columns;
	}

	private _getSelectionDetails(): any {
		const selected_items = this._selection.getSelection();
		const selected_entities = [];

		if (selected_items !== null) {
			for (let i = 0, l = selected_items.length; i < l; i++) {
				const docid = (selected_items[i] as any).id;
				const entity_name = (selected_items[i] as any).entity_name;
				const entityId = (selected_items[i] as any).entityId;
				const selected_entity = { id: docid, name: entity_name, entityId: entityId };
				selected_entities.push(selected_entity);
			}
		}
		return selected_entities.length > 0 ? selected_entities : null;
	}

	Select_file_to = async () => {
		try {
			// Check if there are queued emails - if so, file them instead of current email
			const queuedEmails = getQueuedEmails();
			if (queuedEmails.length > 0) {
				console.log(`Found ${queuedEmails.length} queued emails - using batch filing mode`);
				// Set batch filing mode flag
				sessionStorage.setItem("filing_mode", "queue");
			}
			
			toggleLoading(true, 'Filing...');
			
			const selectedDoc = this._getSelectionDetails();

			const otherData = {
				entity_ids: selectedDoc[0].entityId,
				username: localStorage.getItem("loginUserName") || "",
				fileToType: "note",
				role: sessionStorage.getItem("selected_entity_type") || "client"
			};

			await fileExistingItem(selectedDoc[0].id, otherData);
		} catch (error) {
			console.error("Error in Select_file_to:", error);
			// Clear filing mode on error
			sessionStorage.removeItem('filing_mode');
			toggleLoading(false);
			throw error;
		}
	};

	renderConfirmationDialog() {
		if (!this.state.showConfirmation) {
			return null;
		}

		return (
			<div style={{
				position: 'fixed',
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				backgroundColor: 'rgba(0, 0, 0, 0.5)',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				zIndex: 10000
			}}>
				<div style={{
					backgroundColor: '#ffffff',
					padding: '24px',
					borderRadius: '8px',
					maxWidth: '500px',
					width: '90%',
					boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
				}}>
					<h3 style={{
						margin: '0 0 16px 0',
						fontSize: '16px',
						fontWeight: 600,
						color: '#323130'
					}}>
						You are about to file multiple emails
					</h3>
					<p style={{
						margin: '0 0 24px 0',
						fontSize: '13px',
						color: '#605e5c',
						lineHeight: '1.5'
					}}>
						Are you sure you want to file <strong>{this.state.queuedEmailCount} email{this.state.queuedEmailCount > 1 ? 's' : ''}</strong> to the selected existing note?
					</p>
					<div style={{
						display: 'flex',
						gap: '12px',
						justifyContent: 'flex-end'
					}}>
						<DefaultButton
							data-automation-id='btnCancelFileQueue'
							text='Cancel'
							onClick={this.cancelFileQueue}
							styles={{
								root: {
									minWidth: '80px',
									minHeight: '36px',
									fontSize: '13px',
									borderRadius: '6px',
									border: '2px solid #5D3FD6',
									color: '#5D3FD6',
									fontWeight: 500,
									backgroundColor: '#ffffff'
								},
								rootHovered: {
									backgroundColor: '#f8f7fc',
									borderColor: '#5D3FD6'
								}
							}}
						/>
						<PrimaryButton
							data-automation-id='btnConfirmFileQueue'
							text='File'
							onClick={this.confirmFileQueue}
							styles={{
								root: {
									minWidth: '80px',
									minHeight: '36px',
									fontSize: '13px',
									borderRadius: '6px',
									backgroundColor: '#5D3FD6',
									border: 'none',
									fontWeight: 500
								},
								rootHovered: {
									backgroundColor: '#4a2fb5'
								}
							}}
						/>
					</div>
				</div>
			</div>
		);
	}

	render() {
		if (this.props.hide) { return null; }

		return (
			<div style={{ 
				fontSize: '13px', 
				display: 'flex', 
				flexDirection: 'column', 
				position: 'fixed',
				top: '45px',
				left: 0,
				right: 0,
				bottom: 0,
				width: '100%',
				height: 'calc(100% - 45px)',
				overflow: 'hidden'
			}}>
				{/* Scrollable Content Area */}
				<div style={{ 
					flex: 1, 
					overflowY: 'auto', 
					overflowX: 'hidden',
					padding: '4px',
					paddingBottom: '8px',
					WebkitOverflowScrolling: 'touch'
				}}>
				<div style={{
					backgroundColor: '#fff',
					borderRadius: '8px',
					padding: '8px',
					marginBottom: '8px',
					boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
					border: '2px solid #5D3FD6'
				}}>
					<h3 style={{ 
						marginTop: 0, 
						marginBottom: '6px', 
						fontSize: '14px',
						color: '#1A1F4D',
						fontWeight: 600
					}}>
						Filed Notes
					</h3>
					<p style={{ marginBottom: '8px', color: '#333', fontSize: '13px' }}>
						It looks like this email has previously been filed for this client. Choose an existing note to re-file it, or select &apos;New&apos; to create a new note.
					</p>
					<div style={{ overflow: "auto" as "auto", maxHeight: '400px' }} data-is-scrollable='true'>
						<MarqueeSelection selection={this._selection}>
							<DetailsList
								items={this._getItems()}
								columns={this._getColumns()}
								setKey='set'
								onRenderItemColumn={this._renderItemColumn}
								selectionMode={SelectionMode.single}
								layoutMode={DetailsListLayoutMode.justified}
								selectionPreservedOnEmptyClick={true}
								enterModalSelectionOnTouch={true}
								checkboxVisibility={CheckboxVisibility.always}
								isHeaderVisible={true}
								compact={true}
								selection={this._selection}
							/>
						</MarqueeSelection>
					</div>
				</div>
				</div>
				
				{/* Fixed Button Footer */}
				<div style={{ 
					flexShrink: 0,
					padding: '8px 12px 12px 12px',
					backgroundColor: '#ffffff',
					borderTop: '2px solid #5D3FD6',
					boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
					zIndex: 10
				}}>
				<div style={{ display: 'flex', gap: '12px', justifyContent: 'space-between' }}>
					<DefaultButton 
						data-automation-id='btnNew' 
						text='New' 
						onClick={this.NewDoc}
						styles={{
							root: {
								flex: 1,
								height: '36px',
								border: '2px solid #5D3FD6',
								borderRadius: '6px'
							},
							label: { fontSize: '13px', fontWeight: 500 }
						}}
					/>
					<PrimaryButton 
						data-automation-id='btnUpdate' 
						disabled={!this.state.selected} 
						text='Update' 
						onClick={this.handleUpdateButtonClick}
						styles={{
							root: {
								flex: 1,
								height: '36px',
								backgroundColor: '#5D3FD6',
								border: 'none',
								borderRadius: '6px'
							},
							rootHovered: { backgroundColor: '#4A2FC7' },
							rootPressed: { backgroundColor: '#3B24A8' },
							rootDisabled: { backgroundColor: '#e0e0e0', color: '#666666' },
							label: { fontSize: '13px', fontWeight: 500 }
						}}
					/>
				</div>
				</div>
				{this.renderConfirmationDialog()}
			</div>
		);
	}


	private _renderItemColumn(item: any, _index: number, column: IColumn) {
		const fieldContent = item[column.fieldName || ""];
		const break_string = "|break|";
		if (column.key === "col_entity_name") {
			const split_string = fieldContent.split(break_string);
			return split_string.map((t, i) => <span key={i}>{t}<br /></span>);
		} else {
			return <span>{fieldContent}</span>;
		}
	}
}

export default ExistingDocNote;
